import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { useEffect, useState } from "react";
import SpecialistApi from "../../../api/specialistsApi";
import BlurImageEditor from "../../../components/BlurImageEditor/BlurImageEditor";
import BlurPdfEditor from "../../../components/BlurPdfEditor/BlurPdfEditor";
import Loader from "../../../components/Loader/Loader";
import ModalWindow from "../../../components/ModalWindow/ModalWindow";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { ScrollArea } from "../../../components/ui/scroll-area";
import {
  AppreciationCertificate,
  EducationCertificatesResponse,
  FederationsCertificatesResponse,
  QualificationCertificatesResponse,
} from "../../../types/TypeSpecialist";
import {
  formatCertificateSubSpecializations,
  getTrainingTypeLabel,
} from "../../../utils/certificateMetadata";
import {
  formatDateAndTime,
  formatEducationPeriod,
} from "../../../utils/dateActions";
import { getTranslation } from "../../../utils/getTranslation";
import { getBadgePreviewUrl } from "../../../utils/storedFileReferences";
import Notify, { SuccesNotify } from "../../../utils/toaster";
import UpdateCertificatesModal from "../../Profile/EducationSection/AddCertificatesModal/UpdateCertificatesModal";

type CertificateType =
  | "appreciation"
  | "education"
  | "qualification"
  | "federation";

const connectionString = process.env.REACT_APP_HTTP_CONNECTION_STRING;

interface CertificateComparisonViewProps {
  certificateType: CertificateType;
  specialistId: string;
  mainSpecId: string;
  label: string;
}

export const CertificateComparisonView = ({
  certificateType,
  specialistId,
  label,
  mainSpecId,
}: CertificateComparisonViewProps) => {
  const token = localStorage.getItem("token");
  const [isLoading, setIsLoading] = useState(true);
  const [publicCertificates, setPublicCertificates] = useState<any[]>([]);
  const [pendingCertificates, setPendingCertificates] = useState<any[]>([]);
  const [blurModalOpen, setBlurModalOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<any | null>(
    null
  );
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<{
    pdf: string | null;
    image: string | null;
  }>({
    pdf: null,
    image: null,
  });
  const [editingCertificate, setEditingCertificate] = useState<any | null>(
    null
  );
  const [editModalOpen, setEditModalOpen] = useState(false);
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  useEffect(() => {
    loadCertificates();
  }, [specialistId, certificateType]);

  const loadCertificates = async () => {
    if (!token) return;

    try {
      setIsLoading(true);

      // Load PUBLIC (old/current) certificates
      let publicResponse;
      let pendingResponse;

      switch (certificateType) {
        case "appreciation":
          publicResponse =
            await SpecialistApi.getAppreciationPublicCertificates(
              specialistId,
              1,
              100
            );
          pendingResponse = await SpecialistApi.getAppreciationCertificates(
            token,
            specialistId,
            1,
            100
          );
          break;
        case "education":
          publicResponse = await SpecialistApi.getEducationPublicCertificates(
            specialistId,
            1,
            100
          );
          pendingResponse = await SpecialistApi.getEducationCertificates(
            token,
            specialistId,
            1,
            100
          );
          break;
        case "qualification":
          publicResponse =
            await SpecialistApi.getQualificationPublicCertificates(
              specialistId,
              1,
              100
            );
          pendingResponse = await SpecialistApi.getQualificationCertificates(
            token,
            specialistId,
            1,
            100
          );
          break;
        case "federation":
          publicResponse = await SpecialistApi.getFederationPublicCertificates(
            specialistId,
            1,
            100
          );
          pendingResponse = await SpecialistApi.getFederationCertificates(
            token,
            specialistId,
            1,
            100
          );
          break;
      }

      setPublicCertificates(publicResponse?.certificates || []);
      setPendingCertificates(pendingResponse?.certificates || []);
    } catch (error) {
      console.error("Error loading certificates:", error);
      Notify("Failed to load certificates");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBlurCertificate = (certificate: any) => {
    setSelectedCertificate(certificate);
    setBlurModalOpen(true);
  };

  const handlePreview = (link: string, type: string) => {
    if (type.includes("pdf")) {
      setPreviewFile({ pdf: link, image: null });
    } else {
      setPreviewFile({ pdf: null, image: link });
    }
    setPreviewModalOpen(true);
  };

  const renderMetadataRow = (label: string, value?: string) => {
    if (!value) return null;

    return (
      <div className="text-sm text-gray-400">
        {label}: {value}
      </div>
    );
  };

  const updateCertificateFile = async (newFile: File) => {
    if (!token || !selectedCertificate) return;

    try {
      let response;

      switch (certificateType) {
        case "appreciation":
          // Appreciation has different signature
          response = await SpecialistApi.updateAppreciationCertificate(token, {
            userId: specialistId,
            specialistId: specialistId,
            file: newFile,
            updateData: { _id: selectedCertificate._id },
          });
          break;
        case "education":
          response = await SpecialistApi.updateEducationCertificate(
            {
              file: newFile,
              specialistId: specialistId,
              updateData: { _id: selectedCertificate._id },
            },
            token
          );
          break;
        case "qualification":
          response = await SpecialistApi.updateQualificationCertificate(
            {
              file: newFile,
              specialistId: specialistId,
              updateData: { _id: selectedCertificate._id },
            },
            token
          );
          break;
        case "federation":
          response = await SpecialistApi.updateFederationCertificate(
            {
              file: newFile,
              specialistId: specialistId,
              updateData: { _id: selectedCertificate._id },
            },
            token
          );
          break;
      }

      if (!response?.status && response?.message) {
        Notify(response.message);
        return;
      }

      if (response?.status) {
        setBlurModalOpen(false);
        SuccesNotify("Certificate has been updated with blurred version");
        // Reload certificates to show updated version
        await loadCertificates();
      }
    } catch (error) {
      console.error("Error updating certificate:", error);
      Notify("Failed to update certificate");
    }
  };

  const renderAppreciationCard = (
    cert: AppreciationCertificate,
    isOld: boolean
  ) => (
    <Card
      key={cert._id}
      className={`${
        isOld
          ? "bg-red-900/20 border-red-700"
          : "bg-green-900/20 border-green-700"
      } mb-4`}
    >
      <CardContent className="pt-4">
        <div className="space-y-2">
          <div className="font-semibold text-white">{cert.organization}</div>
          <div className="text-gray-300">{cert.receivedFor}</div>
          <div className="text-sm text-gray-400">
            {formatDateAndTime(new Date(cert.receiveDate), "en").formattedDate}
          </div>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={() =>
                handlePreview(cert.file?.link ?? "", cert.file?.type ?? "")
              }
              disabled={!cert.file?.link}
              variant="outline"
              size="sm"
              className="flex-1"
            >
              View Certificate
            </Button>
            {!isOld && (
              <>
                <Button
                  onClick={() => handleBlurCertificate(cert)}
                  variant="secondary"
                  size="sm"
                  className="flex-1"
                >
                  Blur Surname
                </Button>
                <Button
                  onClick={() => {
                    setEditingCertificate(cert);
                    setEditModalOpen(true);
                  }}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  Edit
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
      {!isOld && (
        <UpdateCertificatesModal
          certificateType="appreciation"
          modalOpen={editModalOpen}
          setModalOpen={setEditModalOpen}
          specialistId={specialistId}
          setupUpdateCertificate={(cert: any) =>
            setPendingCertificates((prev) => {
              if (prev) {
                return prev.map((item) =>
                  item._id === cert._id ? cert : item
                );
              }
              return prev;
            })
          }
          mainSpec={mainSpecId}
          appreciationToUpdate={
            certificateType === "appreciation" ? editingCertificate : undefined
          }
          educationToUpdate={
            certificateType === "education" ? editingCertificate : undefined
          }
          qualificationToUpdate={
            certificateType === "qualification" ? editingCertificate : undefined
          }
          federationToUpdate={
            certificateType === "federation" ? editingCertificate : undefined
          }
        />
      )}
    </Card>
  );

  const renderEducationCard = (
    cert: EducationCertificatesResponse,
    isOld: boolean
  ) => (
    <Card
      key={cert._id}
      className={`${
        isOld
          ? "bg-red-900/20 border-red-700"
          : "bg-green-900/20 border-green-700"
      } mb-4`}
    >
      <CardContent className="pt-4">
        <div className="space-y-2">
          <div className="font-semibold text-white">
            {cert.education.text.find((el) => el.language === "en")?.text ||
              cert.education.text[0]?.text}
          </div>
          <div className="text-gray-300">
            {cert.educationCourse.text.find((el) => el.language === "en")
              ?.text || cert.educationCourse.text[0]?.text}
          </div>
          {cert.studyHours && (
            <div className="text-sm text-gray-400">
              Hours of study: {cert.studyHours} hours
            </div>
          )}
          {renderMetadataRow(
            "Training type",
            getTrainingTypeLabel(cert.trainingType)
          )}
          {renderMetadataRow("Document number", cert.documentNumber)}
          {renderMetadataRow(
            "Sub-specializations",
            formatCertificateSubSpecializations(cert.subSpecializations)
          )}
          {cert.educationAccreditation &&
            cert.educationAccreditation.length > 0 && (
              <div className="text-sm text-gray-400">
                Accreditation:{" "}
                {cert.educationAccreditation
                  .map((accr) => getTranslation(accr.text, "en"))
                  .join(", ")}
              </div>
            )}
          {cert.membershipStart && cert.membershipEnd && (
            <div className="text-sm text-gray-400">
              {formatEducationPeriod(
                new Date(cert.membershipStart),
                new Date(cert.membershipEnd),
                "en"
              )}
            </div>
          )}
          {cert.link ? (
            <div className="flex gap-2 mt-3">
            <Button
                onClick={() => handlePreview(cert.link!, cert.type ?? "")}
                disabled={!cert.link}
                variant="outline"
                size="sm"
                className="flex-1"
              >
                View Certificate
              </Button>
              {!isOld && (
                <>
                  <Button
                    onClick={() => handleBlurCertificate(cert)}
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                  >
                    Blur Surname
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingCertificate(cert);
                      setEditModalOpen(true);
                    }}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    Edit
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="text-sm text-yellow-400 italic">
              In the process of learning
            </div>
          )}
        </div>
      </CardContent>
      {!isOld && (
        <UpdateCertificatesModal
          certificateType="education"
          modalOpen={editModalOpen}
          setModalOpen={setEditModalOpen}
          specialistId={specialistId}
          setupUpdateCertificate={(cert: any) =>
            setPendingCertificates((prev) => {
              if (prev) {
                return prev.map((item) =>
                  item._id === cert._id ? cert : item
                );
              }
              return prev;
            })
          }
          mainSpec={mainSpecId}
          appreciationToUpdate={
            certificateType === "appreciation" ? editingCertificate : undefined
          }
          educationToUpdate={
            certificateType === "education" ? editingCertificate : undefined
          }
          qualificationToUpdate={
            certificateType === "qualification" ? editingCertificate : undefined
          }
          federationToUpdate={
            certificateType === "federation" ? editingCertificate : undefined
          }
        />
      )}
    </Card>
  );

  const renderQualificationCard = (
    cert: QualificationCertificatesResponse,
    isOld: boolean
  ) => (
    <Card
      key={cert._id}
      className={`${
        isOld
          ? "bg-red-900/20 border-red-700"
          : "bg-green-900/20 border-green-700"
      } mb-4`}
    >
      <CardContent className="pt-4">
        <div className="space-y-2">
          {cert.qualification.badge && (
            <div className="mb-2">
              <a
                href={cert.qualification.badge.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={getBadgePreviewUrl({
                    badge: cert.qualification.badge,
                    getLegacyUrl: (fileId) =>
                      `${connectionString}specialists/getFederationBadgeStream/${fileId}`,
                  })}
                  alt="Badge"
                  className="w-16 h-16 object-contain"
                />
              </a>
            </div>
          )}
          <div className="font-semibold text-white">
            {cert.qualification.text.find((el) => el.language === "en")?.text ||
              cert.qualification.text[0]?.text}
          </div>
          {renderMetadataRow("Certificate number", cert.certificateNumber)}
          {renderMetadataRow(
            "Sub-specializations",
            formatCertificateSubSpecializations(cert.subSpecializations)
          )}
          <div className="text-sm text-gray-400">
            {formatEducationPeriod(
              new Date(cert.membershipStart),
              new Date(cert.membershipEnd),
              "en"
            )}
          </div>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={() => handlePreview(cert.link ?? "", cert.type ?? "")}
              disabled={!cert.link}
              variant="outline"
              size="sm"
              className="flex-1"
            >
              View Certificate
            </Button>
            {!isOld && (
              <>
                <Button
                  onClick={() => handleBlurCertificate(cert)}
                  variant="secondary"
                  size="sm"
                  className="flex-1"
                >
                  Blur Surname
                </Button>
                <Button
                  onClick={() => {
                    setEditingCertificate(cert);
                    setEditModalOpen(true);
                  }}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  Edit
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
      {!isOld && (
        <UpdateCertificatesModal
          certificateType="qualification"
          modalOpen={editModalOpen}
          setModalOpen={setEditModalOpen}
          specialistId={specialistId}
          setupUpdateCertificate={(cert: any) =>
            setPendingCertificates((prev) => {
              if (prev) {
                return prev.map((item) =>
                  item._id === cert._id ? cert : item
                );
              }
              return prev;
            })
          }
          mainSpec={mainSpecId}
          appreciationToUpdate={
            certificateType === "appreciation" ? editingCertificate : undefined
          }
          educationToUpdate={
            certificateType === "education" ? editingCertificate : undefined
          }
          qualificationToUpdate={
            certificateType === "qualification" ? editingCertificate : undefined
          }
          federationToUpdate={
            certificateType === "federation" ? editingCertificate : undefined
          }
        />
      )}
    </Card>
  );

  const renderFederationCard = (
    cert: FederationsCertificatesResponse,
    isOld: boolean
  ) => (
    <Card
      key={cert._id}
      className={`${
        isOld
          ? "bg-red-900/20 border-red-700"
          : "bg-green-900/20 border-green-700"
      } mb-4`}
    >
      <CardContent className="pt-4">
        <div className="space-y-2">
          {cert.federation.badge && (
            <div className="mb-2">
              <a
                href={cert.federation.badge.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={getBadgePreviewUrl({
                    badge: cert.federation.badge,
                    getLegacyUrl: (fileId) =>
                      `${connectionString}specialists/getFederationBadgeStream/${fileId}`,
                  })}
                  alt="Badge"
                  className="w-16 h-16 object-contain"
                />
              </a>
            </div>
          )}
          <div className="font-semibold text-white">
            {cert.federation.text.find((el) => el.language === "en")?.text ||
              cert.federation.text[0]?.text}
          </div>
          {renderMetadataRow("Certificate number", cert.certificateNumber)}
          <div className="text-sm text-gray-400">
            {formatEducationPeriod(
              new Date(cert.membershipStart),
              new Date(cert.membershipEnd),
              "en"
            )}
          </div>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={() => handlePreview(cert.link ?? "", cert.type ?? "")}
              disabled={!cert.link}
              variant="outline"
              size="sm"
              className="flex-1"
            >
              View Certificate
            </Button>
            {!isOld && (
              <>
                <Button
                  onClick={() => handleBlurCertificate(cert)}
                  variant="secondary"
                  size="sm"
                  className="flex-1"
                >
                  Blur Surname
                </Button>
                <Button
                  onClick={() => {
                    setEditingCertificate(cert);
                    setEditModalOpen(true);
                  }}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  Edit
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
      {!isOld && (
        <UpdateCertificatesModal
          certificateType="federation"
          modalOpen={editModalOpen}
          setModalOpen={setEditModalOpen}
          specialistId={specialistId}
          setupUpdateCertificate={(cert: any) =>
            setPendingCertificates((prev) => {
              if (prev) {
                return prev.map((item) =>
                  item._id === cert._id ? cert : item
                );
              }
              return prev;
            })
          }
          mainSpec={mainSpecId}
          appreciationToUpdate={
            certificateType === "appreciation" ? editingCertificate : undefined
          }
          educationToUpdate={
            certificateType === "education" ? editingCertificate : undefined
          }
          qualificationToUpdate={
            certificateType === "qualification" ? editingCertificate : undefined
          }
          federationToUpdate={
            certificateType === "federation" ? editingCertificate : undefined
          }
        />
      )}
    </Card>
  );

  const renderCertificate = (cert: any, isOld: boolean) => {
    switch (certificateType) {
      case "appreciation":
        return renderAppreciationCard(cert, isOld);
      case "education":
        return renderEducationCard(cert, isOld);
      case "qualification":
        return renderQualificationCard(cert, isOld);
      case "federation":
        return renderFederationCard(cert, isOld);
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader size={40} />
      </div>
    );
  }

  return (
    <div className="dark bg-gray-900 p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white mb-2">
          {label} Comparison
        </h3>
        <div className="flex gap-2">
          <Badge variant="secondary">
            Public: {publicCertificates.length} certificate(s)
          </Badge>
          <Badge variant="secondary">
            Pending: {pendingCertificates.length} certificate(s)
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* LEFT: Public (OLD) Certificates */}
        <div>
          <div className="mb-2 text-sm font-medium text-red-400">
            Current (Public) - {publicCertificates.length}
          </div>
          <ScrollArea className="h-[600px] pr-4">
            {publicCertificates.length > 0 ? (
              publicCertificates.map((cert) => renderCertificate(cert, true))
            ) : (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <p className="text-center text-gray-400">
                    No public certificates
                  </p>
                </CardContent>
              </Card>
            )}
          </ScrollArea>
        </div>

        {/* RIGHT: Pending (NEW) Certificates */}
        <div>
          <div className="mb-2 text-sm font-medium text-green-400">
            Updated (Pending) - {pendingCertificates.length}
          </div>
          <ScrollArea className="h-[600px] pr-4">
            {pendingCertificates.length > 0 ? (
              pendingCertificates.map((cert) => renderCertificate(cert, false))
            ) : (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <p className="text-center text-gray-400">
                    No pending certificates
                  </p>
                </CardContent>
              </Card>
            )}
          </ScrollArea>
        </div>
      </div>

      {/* Blur Modal */}
      {selectedCertificate && (
        <ModalWindow
          isOpen={blurModalOpen}
          setIsOpen={setBlurModalOpen}
          width="fit-content"
          bgColor="transparent"
          height="fit-content"
          padding="20px"
          maxWidth="90vw"
        >
          {selectedCertificate.type === "pdf" ||
          selectedCertificate.file?.type === "pdf" ? (
            <BlurPdfEditor
              pdfUrl={
                selectedCertificate.link || selectedCertificate.file?.link
              }
              savePdf={updateCertificateFile}
            />
          ) : (
            <BlurImageEditor
              imageUrl={
                selectedCertificate.link || selectedCertificate.file?.link
              }
              saveImage={updateCertificateFile}
            />
          )}
        </ModalWindow>
      )}

      {/* Preview Modal */}
      <ModalWindow
        isOpen={previewModalOpen}
        setIsOpen={setPreviewModalOpen}
        bgColor="transparent"
        height="fit-content"
        width="fit-content"
        padding="0"
        maxWidth="90vw"
      >
        <div className="bg-gray-900 p-4 rounded-lg">
          {previewFile.pdf && (
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.js">
              <div style={{ height: "80vh", width: "70vw" }}>
                <Viewer
                  fileUrl={previewFile.pdf}
                  plugins={[defaultLayoutPluginInstance]}
                />
              </div>
            </Worker>
          )}

          {previewFile.image && (
            <img
              src={previewFile.image}
              alt="Certificate"
              className="max-w-full max-h-[80vh] object-contain"
            />
          )}
        </div>
      </ModalWindow>
    </div>
  );
};
